import type { FormsApiClient } from '../api';
import type { LeadFormPublicConfig, RecommendedProductSummary } from '../types';

/** Fetches `/recommendations` (only meaningful when `theme.recommendations` is set — see
 * LeadRecommendationService on the backend, which resolves the actual product list) and renders a
 * row of product cards linking out to each one's page. Returns the (initially empty) container
 * immediately so callers can slot it into their panel synchronously; population happens
 * fire-and-forget once the fetch resolves, same non-blocking shape frequencyCap/variant state
 * already use elsewhere in this SDK. */
export function buildRecommendations(form: LeadFormPublicConfig, client: FormsApiClient): HTMLElement {
  const container = document.createElement('div');
  container.className = 'recommendations';

  client.fetchRecommendations(form.slug).then((products) => {
    if (products.length === 0) return;
    for (const product of products) {
      container.appendChild(buildCard(product));
    }
  });

  return container;
}

function buildCard(product: RecommendedProductSummary): HTMLElement {
  const card = document.createElement('a');
  card.className = 'rec-card';
  card.href = product.pageUrl || '#';
  card.target = '_top';

  if (product.imageUrl) {
    const img = document.createElement('img');
    img.src = product.imageUrl;
    img.alt = '';
    card.appendChild(img);
  }

  if (product.title) {
    const title = document.createElement('span');
    title.className = 'rec-title';
    title.textContent = product.title;
    card.appendChild(title);
  }

  const price = document.createElement('span');
  price.className = 'rec-price';
  price.textContent = formatPrice(product.priceCents, product.currency);
  if (product.compareAtPrice != null && product.compareAtPrice > product.priceCents) {
    const compare = document.createElement('span');
    compare.className = 'rec-compare';
    compare.textContent = formatPrice(product.compareAtPrice, product.currency);
    price.appendChild(compare);
  }
  card.appendChild(price);

  return card;
}

function formatPrice(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}
